<script setup lang="ts">
import type { VNodeChild } from 'vue'
import { NButton, NDropdown, type DropdownOption } from 'naive-ui'
import type { DropdownMixedOption, MenuKey } from '../composables/useHeaderMenu'

// The mobile hamburger trigger and its dropdown, extracted from
// WikiHeaderActions to keep that file under the 300-line limit. Only the
// badge / running-dot rendering lives here; the options and the select
// dispatcher come from the parent's useHeaderMenu.
defineProps<{
  options: DropdownMixedOption[]
  renderLabel: (option: DropdownOption) => VNodeChild
  label: string
  isAdmin: boolean
  adminOpenCount: number
  runningSessionCount: number
}>()

const emit = defineEmits<{
  (e: 'select', key: MenuKey): void
}>()

function onSelect(key: MenuKey) {
  emit('select', key)
}
</script>

<template>
  <NDropdown trigger="click" :options="options" :render-label="renderLabel" @select="onSelect">
    <NButton quaternary size="small" data-testid="mobile-menu" :aria-label="label">
      <span class="relative inline-flex">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
        <span
          v-if="isAdmin && adminOpenCount > 0"
          data-testid="mobile-admin-open-badge"
          aria-hidden="true"
          class="absolute -top-1.5 -right-2 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-status-alert text-[10px] font-semibold text-black leading-none"
          >{{ adminOpenCount }}</span
        >
        <span
          v-else-if="isAdmin && runningSessionCount > 0"
          data-testid="mobile-admin-running-dot"
          aria-hidden="true"
          class="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-brand-primary animate-pulse"
        />
      </span>
    </NButton>
  </NDropdown>
</template>
